import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Heading, PageContainer, Section } from '../../../shared/ui';
import { submitYowanPublicNewsSignup } from '../app/yowanContentClient';
import {
  executeYowanRecaptcha,
  getYowanRecaptchaErrorMessage,
  getYowanRecaptchaResponseErrorMessage,
  YOWAN_RECAPTCHA_ACTIONS,
} from '../app/yowanRecaptchaClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const getFieldErrorMessage = (value) => {
  if (Array.isArray(value)) {
    return value.join(' ');
  }

  return typeof value === 'string' ? value : '';
};

const MailMagazinePage = () => {
  const content = yowanPageContent['/mail-magazine'];
  const metadata = getYowanRouteMeta('/mail-magazine');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      name_kana: '',
      email: '',
      attended: '',
      source: '',
      message: '',
    },
  });

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      const recaptchaToken = await executeYowanRecaptcha(
        YOWAN_RECAPTCHA_ACTIONS.newsSignup,
      );

      await submitYowanPublicNewsSignup({
        ...values,
        recaptcha_token: recaptchaToken,
      });
      reset();
      setSubmitMessage(content.form.successMessage);
    } catch (error) {
      const localRecaptchaMessage = getYowanRecaptchaErrorMessage(error?.code);

      if (localRecaptchaMessage) {
        setSubmitMessage(localRecaptchaMessage);
        return;
      }

      const responseData = error?.response?.data || {};
      const fieldNames = ['email', 'name', 'name_kana', 'attended', 'source', 'message'];

      fieldNames.forEach((fieldName) => {
        const message = getFieldErrorMessage(responseData[fieldName]);

        if (message) {
          setError(fieldName, { type: 'server', message });
        }
      });

      setSubmitMessage(
        getYowanRecaptchaResponseErrorMessage(responseData) ||
          responseData.detail ||
          content.form.errorMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasGuidance =
    content.guidance.paragraphs.length > 0 || content.guidance.channels.length > 0;

  return (
    <>
      <Section>
        <PageContainer>
          <div className="yowan-contact-header">
            <div className="yowan-contact-header__body">
              <Heading as="h1">{content.header.title}</Heading>
              {content.header.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className={`yowan-contact-layout${hasGuidance ? '' : ' yowan-contact-layout--single'}`}>
            {hasGuidance ? (
              <div className="yowan-contact-guidance c-surface-content">
                {content.guidance.paragraphs.length ? (
                  <div className="yowan-contact-guidance__body">
                    {content.guidance.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                ) : null}
                {content.guidance.channels.length ? (
                  <dl className="yowan-contact-guidance__channels">
                    {content.guidance.channels.map((channel) => (
                      <div key={channel.label} className="yowan-contact-guidance__channel">
                        <dt>{channel.label}</dt>
                        <dd>
                          {channel.href && channel.external ? (
                            <a href={channel.href} target="_blank" rel="noreferrer">
                              {channel.value}
                            </a>
                          ) : channel.href ? (
                            <a href={channel.href}>{channel.value}</a>
                          ) : (
                            channel.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            ) : null}
            <form
              className="yowan-contact-form yowan-mail-magazine-form c-surface-content c-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              {content.form.lead ? (
                <p className="yowan-contact-form__lead">{content.form.lead}</p>
              ) : null}

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">
                  {content.form.labels.name}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="text"
                  {...register('name', { required: content.form.errors.required })}
                />
                {errors.name ? (
                  <em className="yowan-contact-form__error">{errors.name.message}</em>
                ) : null}
              </label>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">
                  {content.form.labels.nameKana}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="text"
                  {...register('name_kana', { required: content.form.errors.required })}
                />
                {errors.name_kana ? (
                  <em className="yowan-contact-form__error">{errors.name_kana.message}</em>
                ) : null}
              </label>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">
                  {content.form.labels.email}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="email"
                  {...register('email', { required: content.form.errors.required })}
                />
                {errors.email ? (
                  <em className="yowan-contact-form__error">{errors.email.message}</em>
                ) : null}
              </label>

              <div className="yowan-contact-form__field c-form__field c-form__field--choice">
                <span className="c-form__label">
                  {content.form.labels.attended}
                  <em className="c-form__required">*</em>
                </span>
                <div className="c-form__choices">
                  {content.form.options.attended.map((option) => (
                    <label key={option} className="c-form__choice">
                      <input
                        type="radio"
                        value={option}
                        {...register('attended', { required: content.form.errors.required })}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
                {errors.attended ? (
                  <em className="yowan-contact-form__error">{errors.attended.message}</em>
                ) : null}
              </div>

              <div className="yowan-contact-form__field c-form__field c-form__field--choice">
                <span className="c-form__label">
                  {content.form.labels.source}
                  <em className="c-form__required">*</em>
                </span>
                <div className="c-form__choices">
                  {content.form.options.source.map((option) => (
                    <label key={option} className="c-form__choice">
                      <input
                        type="radio"
                        value={option}
                        {...register('source', { required: content.form.errors.required })}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
                {errors.source ? (
                  <em className="yowan-contact-form__error">{errors.source.message}</em>
                ) : null}
              </div>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">{content.form.labels.message}</span>
                <textarea
                  className="c-form__control c-form__control--textarea"
                  rows="4"
                  {...register('message')}
                />
              </label>

              <button
                type="submit"
                className="yowan-contact-form__submit c-form__submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
              </button>
              {submitMessage ? (
                <p className="yowan-contact-form__message">{submitMessage}</p>
              ) : null}
            </form>
          </div>
        </PageContainer>
      </Section>
    </>
  );
};

export default MailMagazinePage;
